import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { categories, comics } from '@/mocks/comics';
import ComicCard from '@/pages/home/components/ComicCard';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'update'>('rating');

  const currentCategory = useMemo(() => {
    return categories.find(c => c.slug === slug) || categories[0];
  }, [slug]);

  const filteredComics = useMemo(() => {
    if (!slug || slug === 'all') return comics;
    return comics.filter(c => c.category.includes(slug));
  }, [slug]);

  const sortedComics = useMemo(() => {
    const list = [...filteredComics];
    if (sortBy === 'rating') {
      return list.sort((a, b) => b.rating - a.rating);
    }
    if (sortBy === 'views') {
      return list.sort((a, b) => b.views - a.views);
    }
    return list.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  }, [filteredComics, sortBy]);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://www.koreanmanhwa.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `${currentCategory.name}漫画`,
        "item": `https://www.koreanmanhwa.cn/category/${slug}`
      }
    ]
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory.name}韩国漫画 - 韩国漫画网`,
    "description": `韩国漫画网${currentCategory.name}分类，收录最好看的${currentCategory.name}类韩国漫画，支持韩漫在线免费阅读`,
    "url": `https://www.koreanmanhwa.cn/category/${slug}`
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />

      <header className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800">{currentCategory.name}漫画</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            {currentCategory.name === '全部' ? '全部韩国漫画' : `${currentCategory.name}韩国漫画`}
          </h1>
          <p className="text-foreground-500 text-sm mt-2">
            共找到 <strong className="text-primary-600">{filteredComics.length}</strong> 部漫画
          </p>
        </div>
      </header>

      <section className="py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200/70">
            <span className="text-sm text-foreground-500 mr-2">分类：</span>
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  currentCategory.slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              <strong className="text-foreground-800">{filteredComics.length}</strong> 部漫画
            </p>
            <div className="flex items-center gap-1">
              <span className="text-xs text-foreground-500 mr-2">排序：</span>
              {[
                { key: 'rating' as const, label: '评分最高' },
                { key: 'views' as const, label: '人气最热' },
                { key: 'update' as const, label: '最近更新' },
              ].map(option => (
                <button
                  key={option.key}
                  onClick={() => setSortBy(option.key)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === option.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {sortedComics.map(comic => (
                <ComicCard key={comic.id} comic={comic} size="small" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              <Link
                to="/category/all"
                className="inline-block mt-4 px-6 py-2 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}